/**
 * Pure {@link TurnTransform} combinators. Each factory returns a transform that
 * produces a NEW {@link TurnRequest} via spread — never a mutation. Combine them
 * with {@link pipe}, which folds a list of transforms left to right, awaiting
 * each (transforms may be async).
 */

import type { CallSettings, LanguageModel, ModelMessage } from 'ai';

import type { TurnRequest, TurnTransform } from './types.ts';

/** Pin the model to a single named tool for the next turn. */
export function forceTool(name: string): TurnTransform {
  return (req) => ({ ...req, toolChoice: { type: 'tool', toolName: name } });
}

/** Require the model to call some tool, without pinning which one. */
export function requireTool(): TurnTransform {
  return (req) => ({ ...req, toolChoice: 'required' });
}

/** Forbid tool calls for the next turn (text-only completion). */
export function noTool(): TurnTransform {
  return (req) => ({ ...req, toolChoice: 'none' });
}

/** Narrow the offered tool set to `names` without replacing the registry. */
export function narrowTools(names: string[]): TurnTransform {
  return (req) => ({ ...req, activeTools: names });
}

/** Append messages to the end of the transcript. */
export function appendMessages(msgs: ModelMessage[]): TurnTransform {
  return (req) => ({ ...req, messages: [...req.messages, ...msgs] });
}

/** Prepend messages to the start of the transcript. */
export function prependMessages(msgs: ModelMessage[]): TurnTransform {
  return (req) => ({ ...req, messages: [...msgs, ...req.messages] });
}

/** Splice messages: remove `deleteCount` at `at`, insert `items` in their place. */
export function spliceMessages(at: number, deleteCount: number, items: ModelMessage[]): TurnTransform {
  return (req) => ({ ...req, messages: req.messages.toSpliced(at, deleteCount, ...items) });
}

/** Replace the system prompt for the next turn. */
export function withSystem(s: string): TurnTransform {
  return (req) => ({ ...req, system: s });
}

/** Swap the model for the next turn (flow-layer-only — not serializable). */
export function withModel(model: LanguageModel): TurnTransform {
  return (req) => ({ ...req, model });
}

/** Merge a partial set of call settings over the current ones. */
export function withCallSettings(partial: Partial<CallSettings>): TurnTransform {
  return (req) => ({ ...req, callSettings: { ...req.callSettings, ...partial } });
}

/**
 * Compose transforms into one. Folds left to right, awaiting each — so a later
 * transform sees the cumulative result of every earlier one.
 */
export function pipe(...transforms: TurnTransform[]): TurnTransform {
  return (req: TurnRequest) =>
    transforms.reduce<Promise<TurnRequest>>((acc, transform) => acc.then(transform), Promise.resolve(req));
}
